// src/components/Common/DecorationSelector.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { auth } from '../../config/firebase.ts';
import { invalidateDecorationCache } from './PlayerName.tsx';
import './DecorationSelector.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';

interface UnlockedDecoration {
  id: string;
  name: string;
  description: string;
  emoji: string;
  cssClass: string;
  gradient: string;
  seasonal: string | null;
  active: boolean;
  unlockedAt: string;
}

interface DecorationSelectorProps {
  userName: string;
  onClose?: () => void;
  /** Called after equip/unequip so parent can refresh */
  onChange?: () => void;
}

const DecorationSelector: React.FC<DecorationSelectorProps> = ({ userName, onClose, onChange }) => {
  const [decorations, setDecorations] = useState<UnlockedDecoration[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchDecorations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const user = auth.currentUser;
      if (!user) return;

      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/decorations/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Erreur de chargement');

      const data = await res.json();
      setDecorations(data.unlocked || []);
    } catch (e) {
      setError('Impossible de charger les décorations');
      console.error('[DecorationSelector] fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDecorations();
  }, [fetchDecorations]);

  const handleEquip = async (effectId: string) => {
    try {
      setActionLoading(effectId);
      const user = auth.currentUser;
      if (!user) return;

      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/decorations/equip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ effectId }),
      });

      if (!res.ok) throw new Error('Erreur');

      // Update local state
      setDecorations(prev =>
        prev.map(d => ({ ...d, active: d.id === effectId }))
      );

      // Invalidate cache so PlayerName re-fetches
      invalidateDecorationCache(user.uid);
      onChange?.();
    } catch (e) {
      console.error('[DecorationSelector] equip error:', e);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnequip = async () => {
    try {
      setActionLoading('__unequip__');
      const user = auth.currentUser;
      if (!user) return;

      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/decorations/unequip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error('Erreur');

      // Update local state
      setDecorations(prev => prev.map(d => ({ ...d, active: false })));

      // Invalidate cache
      invalidateDecorationCache(user.uid);
      onChange?.();
    } catch (e) {
      console.error('[DecorationSelector] unequip error:', e);
    } finally {
      setActionLoading(null);
    }
  };

  const activeDeco = decorations.find(d => d.active);

  if (loading) {
    return (
      <div className="deco-selector">
        <div className="deco-selector-header">
          <h3>🎨 Mes Décorations</h3>
          {onClose && <button className="deco-close-btn" onClick={onClose}>✕</button>}
        </div>
        <div className="deco-loading">
          <div className="deco-spinner" />
          <span>Chargement…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="deco-selector">
        <div className="deco-selector-header">
          <h3>🎨 Mes Décorations</h3>
          {onClose && <button className="deco-close-btn" onClick={onClose}>✕</button>}
        </div>
        <div className="deco-error">
          <span>⚠️ {error}</span>
          <button onClick={fetchDecorations} className="deco-retry-btn">Réessayer</button>
        </div>
      </div>
    );
  }

  return (
    <div className="deco-selector">
      <div className="deco-selector-header">
        <h3>🎨 Mes Décorations</h3>
        {onClose && <button className="deco-close-btn" onClick={onClose}>✕</button>}
      </div>

      {/* Preview */}
      <div className="deco-preview">
        <span className="deco-preview-label">Aperçu :</span>
        <div className="deco-preview-name">
          {activeDeco ? (
            <span
              className={`player-name player-deco ${activeDeco.cssClass}`}
              style={{ '--deco-gradient': activeDeco.gradient } as React.CSSProperties}
            >
              <span className="deco-icon-left">{activeDeco.emoji}</span>
              <span className="deco-name-text" style={{ '--deco-gradient': activeDeco.gradient } as React.CSSProperties}>
                {userName}
              </span>
              <span className="deco-icon-right">{activeDeco.emoji}</span>
            </span>
          ) : (
            <span className="player-name">{userName}</span>
          )}
        </div>
      </div>

      {decorations.length === 0 ? (
        <div className="deco-empty">
          <span className="deco-empty-icon">🎁</span>
          <p>Aucune décoration débloquée</p>
          <p className="deco-empty-hint">
            Participez aux événements saisonniers pour débloquer des décorations !
          </p>
        </div>
      ) : (
        <>
          {/* List */}
          <div className="deco-list">
            {/* Option "Aucune" */}
            <div
              className={`deco-item ${!activeDeco ? 'active' : ''}`}
              onClick={() => activeDeco && handleUnequip()}
            >
              <div className="deco-item-icon">🚫</div>
              <div className="deco-item-info">
                <span className="deco-item-name">Aucune décoration</span>
                <span className="deco-item-desc">Pseudo sans effet</span>
              </div>
              <div className="deco-item-status">
                {actionLoading === '__unequip__' ? (
                  <div className="deco-mini-spinner" />
                ) : !activeDeco ? (
                  <span className="deco-badge-active">Actif</span>
                ) : null}
              </div>
            </div>

            {decorations.map(deco => (
              <div
                key={deco.id}
                className={`deco-item ${deco.active ? 'active' : ''}`}
                onClick={() => !deco.active && handleEquip(deco.id)}
              >
                <div className="deco-item-icon">{deco.emoji}</div>
                <div className="deco-item-info">
                  <span className="deco-item-name">{deco.name}</span>
                  <span className="deco-item-desc">{deco.description}</span>
                  {deco.seasonal && (
                    <span className="deco-item-seasonal">
                      {deco.seasonal === 'christmas' && '🎄 Noël'}
                      {deco.seasonal === 'valentine' && '💕 Saint-Valentin'}
                      {deco.seasonal === 'halloween' && '🎃 Halloween'}
                    </span>
                  )}
                </div>
                <div className="deco-item-status">
                  {actionLoading === deco.id ? (
                    <div className="deco-mini-spinner" />
                  ) : deco.active ? (
                    <span className="deco-badge-active">Actif</span>
                  ) : (
                    <span className="deco-badge-equip">Équiper</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default DecorationSelector;
